import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
	Avatar,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableRow,
	Tabs,
	Typography,
} from "@mui/material";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useEffect, useState } from "react";
import { ComissionServices, deriveLivveUiStatus, LivveInvoiceRow, LivveUiStatus } from "../../../../shared/services/api/comission/ComissionServices";
import {
	globalFormatCurrencyBRL,
	globalMaskDocument,
	globalTranslateStatusComission,
	globalTranslateTypeComission,
	translateProposal,
} from "../../../../shared/utils";
import { globalcolorBackGroundComission, globalcolorTextComision } from "../../../../shared/utils/global-utils-comission";

const statusLabel: Record<LivveUiStatus, string> = {
	ready: "Pronta pra enviar",
	sent: "Enviada",
	paid: "Paga",
};

const statusColor: Record<LivveUiStatus, { bg: string; text: string }> = {
	ready: { bg: "#f3f4f6", text: "#374151" },
	sent: { bg: "#eef2ff", text: "#4338ca" },
	paid: { bg: "#e9f8f5", text: "#0f6e56" },
};

type DetailItemProps = {
	label: string;
	value?: string | null;
	highlight?: boolean;
};

const DetailItem = ({ label, value, highlight = false }: DetailItemProps) => (
	<Box sx={{ py: 1.1 }}>
		<Typography variant="caption" sx={{ color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
			{label}
		</Typography>
		<Typography variant="body2" sx={{ mt: 0.4, fontWeight: highlight ? 700 : 500, color: "#1f2937", wordBreak: "break-word" }}>
			{value || "-"}
		</Typography>
	</Box>
);

const SectionTitle = ({ title, subtitle }: { title: string; subtitle?: string }) => (
	<Box sx={{ mb: 1.5 }}>
		<Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
			{title}
		</Typography>
		{subtitle && (
			<Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
				{subtitle}
			</Typography>
		)}
	</Box>
);

// Mesmo formato retornado por /comission/values/open (usado também em ModalDetailComission) — não
// é o ComissionStatusData genérico de ComissionServices.ts, tem campos denormalizados da proposal.
interface ValueItem {
	id: string;
	proposalCertificateNumber: string | null;
	proposalName: string | null;
	proposalDocument?: string;
	type: string;
	installmentNumber?: number;
	proposalRentMonthsCount?: number;
	value: string;
	dueDate: string;
	description: string;
	status: string;
	// Ciclo em que o item foi apurado pelo close-cycle (ex: "2026-09") — confirmado no manual v2.1,
	// seção 5.1. Existe 1 Comission por imobiliária/ciclo (seção 2.5), então isso já identifica os
	// itens desta fatura sem precisar de invoiceReference (campo não documentado, removido daqui).
	settledCycle?: string;
}

type DetailTab = "detalhes" | "valores";

interface Props {
	open: boolean;
	onClose: () => void;
	row: LivveInvoiceRow;
}

export const ModalDetailLivve = ({ open, onClose, row }: Props) => {
	const uiStatus = deriveLivveUiStatus(row);
	const formatDate = (value?: string | null) => (value ? format(new Date(value), "dd/MM/yyyy") : "-");

	const [tab, setTab] = useState<DetailTab>("detalhes");
	const [items, setItems] = useState<ValueItem[]>([]);
	const [isLoadingItems, setIsLoadingItems] = useState(false);

	useEffect(() => {
		if (!open) return;
		setTab("detalhes");

		const fetchItems = async () => {
			setIsLoadingItems(true);
			try {
				const cycleStart = new Date(`${row.reference}-01T03:00:00`);
				const dataIni = format(startOfMonth(cycleStart), "yyyy-MM-dd");
				const dataFim = format(endOfMonth(cycleStart), "yyyy-MM-dd");
				const result = await ComissionServices.getComissionValuesDetail(row.clientId, dataIni, dataFim);
				const all: ValueItem[] = Array.isArray((result as any)?.results) ? (result as any).results : [];
				// A busca é por cliente + mês inteiro — filtra só os itens já apurados neste ciclo
				// (settledCycle), excluindo itens "preview" do mesmo período que não fazem parte
				// desta fatura (ex: planos sem meta, ainda não decididos pelo close-cycle).
				setItems(all.filter((item) => item.settledCycle === row.reference));
			} catch (error) {
				console.error("Erro ao buscar valores do ciclo:", error);
				setItems([]);
			} finally {
				setIsLoadingItems(false);
			}
		};

		fetchItems();
	}, [open, row.clientId, row.reference]);

	const totalItens = items.reduce((acc, item) => acc + Number(item.value), 0);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
				<Typography variant="h6" fontWeight={600} color="#111827">
					Fatura Livve
				</Typography>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers sx={{ backgroundColor: "#fafafa" }}>
				<Box
					sx={{
						background: "linear-gradient(135deg, rgba(1,112,179,0.10) 0%, rgba(1,112,179,0.03) 100%)",
						border: "1px solid rgba(1,112,179,0.12)",
						borderRadius: "16px",
						p: { xs: 2, md: 2.5 },
						mb: 3,
					}}
				>
					<Box
						display="flex"
						justifyContent="space-between"
						alignItems={{ xs: "flex-start", md: "center" }}
						flexDirection={{ xs: "column", md: "row" }}
						gap={2}
					>
						<Box display="flex" alignItems="center" gap={1.5}>
							<Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>{row.clientName?.trim()?.charAt(0) || "C"}</Avatar>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
									{row.clientName}
								</Typography>
								<Typography variant="body2" sx={{ color: "#6b7280" }}>
									{globalMaskDocument(row.clientDocument)} · Ciclo {row.reference}
								</Typography>
							</Box>
						</Box>
						<Box display="flex" alignItems={{ xs: "flex-start", md: "center" }} gap={3} flexDirection={{ xs: "column", md: "row" }}>
							<Box sx={{ textAlign: { xs: "left", md: "right" } }}>
								<Typography variant="caption" sx={{ color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
									Status
								</Typography>
								<Stack direction="row" alignItems="center" spacing={0.6} justifyContent={{ xs: "flex-start", md: "flex-end" }} sx={{ mt: 0.5 }}>
									<FiberManualRecordIcon sx={{ fontSize: 10, color: statusColor[uiStatus].text }} />
									<Typography variant="body2" sx={{ fontWeight: 600, color: statusColor[uiStatus].text }}>
										{statusLabel[uiStatus]}
									</Typography>
								</Stack>
							</Box>
							<Box sx={{ textAlign: { xs: "left", md: "right" } }}>
								<Typography variant="caption" sx={{ color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
									Valor da fatura
								</Typography>
								<Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main", lineHeight: 1.2 }}>
									{globalFormatCurrencyBRL(row.totValue)}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>

				<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: "1px solid #e5e7eb" }}>
					<Tab icon={<ReceiptLongOutlinedIcon fontSize="small" />} iconPosition="start" label="Detalhes da fatura" value="detalhes" />
					<Tab icon={<ListAltOutlinedIcon fontSize="small" />} iconPosition="start" label={`Valores do ciclo (${items.length})`} value="valores" />
				</Tabs>

				{tab === "detalhes" && (
					<Box
						sx={{
							backgroundColor: "#fff",
							border: "1px solid #e5e7eb",
							borderRadius: "12px",
							p: { xs: 2, md: 3 },
						}}
					>
						<Grid container spacing={4}>
							<Grid item xs={12} md={6}>
								<SectionTitle title="Fatura" subtitle="Identificação e prazos" />
								<DetailItem label="Referência (ciclo)" value={row.reference} />
								<DetailItem label="Valor" value={globalFormatCurrencyBRL(row.totValue)} highlight />
								<DetailItem label="Vencimento" value={formatDate(row.dueDate)} />
								<DetailItem label="Data de pagamento" value={formatDate(row.paymentDate)} />
							</Grid>

							<Grid item xs={12} md={6}>
								<SectionTitle title="Integração Livve" subtitle="Pedido gerado na plataforma de pontos" />
								<DetailItem label="Pedido #" value={row.livveOrderId ?? "-"} />
								<DetailItem label="Status do pedido" value={row.livveOrderStatus ?? "-"} />
								<DetailItem label="Gateway" value={row.gateway ?? "-"} />

								{row.livveBoletoUrl && (
									<Button
										href={row.livveBoletoUrl}
										target="_blank"
										rel="noreferrer"
										variant="outlined"
										size="small"
										startIcon={<DescriptionOutlinedIcon fontSize="small" />}
										endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
										sx={{ mt: 1.5 }}
									>
										Ver boleto
									</Button>
								)}
							</Grid>
						</Grid>
					</Box>
				)}

				{tab === "valores" && (
					<Box
						sx={{
							backgroundColor: "#fff",
							border: "1px solid #e5e7eb",
							borderRadius: "12px",
							overflow: "hidden",
						}}
					>
						{isLoadingItems ? (
							<Stack alignItems="center" py={5}>
								<CircularProgress size={28} />
								<Typography variant="body2" mt={2} color="textSecondary">
									Carregando valores do ciclo...
								</Typography>
							</Stack>
						) : (
							<Box sx={{ maxHeight: 380, overflowY: "auto" }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow sx={{ backgroundColor: "grey.100" }}>
											{["Certificado", "Nome", "Tipo", "Valor", "Vencimento", "Observação", "Status"].map((col, i) => (
												<TableCell key={i}>
													<Typography variant="button" fontSize="0.75rem">
														{col}
													</Typography>
												</TableCell>
											))}
										</TableRow>
									</TableHead>

									<TableBody>
										{items.map((item) => (
											<TableRow key={item.id} hover>
												<TableCell>{item.proposalCertificateNumber ?? "-"}</TableCell>
												<TableCell>
													<Typography fontSize="0.875rem">{item.proposalName}</Typography>
													<Typography fontSize="0.8rem" color="textSecondary">
														{globalMaskDocument(item.proposalDocument ?? "")}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography fontSize="0.875rem">{globalTranslateTypeComission(item.type)}</Typography>
													{item.type === "prize" && item.installmentNumber && item.proposalRentMonthsCount && (
														<Typography variant="caption" color="textSecondary">
															{item.installmentNumber}/{item.proposalRentMonthsCount}
														</Typography>
													)}
												</TableCell>
												<TableCell>
													<Typography fontWeight="bold">{globalFormatCurrencyBRL(item.value)}</Typography>
												</TableCell>
												<TableCell>{formatDate(item.dueDate)}</TableCell>
												<TableCell>
													<Typography fontSize="0.8rem" color="textSecondary">
														{item.description || "-"}
													</Typography>
												</TableCell>
												<TableCell>
													<Chip
														label={translateProposal(globalTranslateStatusComission(item.status))}
														size="small"
														variant="filled"
														sx={{
															backgroundColor: globalcolorBackGroundComission(item.status),
															color: globalcolorTextComision(item.status),
															fontWeight: "normal",
														}}
													/>
												</TableCell>
											</TableRow>
										))}

										{items.length === 0 && (
											<TableRow>
												<TableCell colSpan={7}>
													<Typography variant="body2" color="textSecondary" textAlign="center" py={4}>
														Nenhum valor encontrado pra este ciclo.
													</Typography>
												</TableCell>
											</TableRow>
										)}
									</TableBody>

									{items.length > 0 && (
										<TableFooter>
											<TableRow sx={{ backgroundColor: "#f4fff4" }}>
												<TableCell colSpan={3}>
													<Typography fontWeight={600}>Total ({items.length} item(ns))</Typography>
												</TableCell>
												<TableCell colSpan={4}>
													<Typography fontWeight={700} color="primary">
														{globalFormatCurrencyBRL(totalItens.toString())}
													</Typography>
												</TableCell>
											</TableRow>
										</TableFooter>
									)}
								</Table>
							</Box>
						)}
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};
